import { StepBadge, Typography, cardBoxShadowLight } from '@amelio/ui-library';

import { ConnectorLine } from './components/ConnectorLine';
import {
    getComputedStatus,
    getInteractionClasses,
    getVariant,
    getLabelColor,
} from './utils/StepItemUtils';

import type { StepperSize, SizeConfig, StepperColors } from './types';
import type { StepBadgeProps } from '@amelio/ui-library';
import type { ReactNode } from 'react';

/**
 * Individual step in the stepper.
 */
export interface Step {
    /** Unique identifier for the step. */
    id: string;
    /** Display text for the step. */
    label: string;
    /** Optional subtitle or helper text for the step. */
    description?: string;
    /** Current status of the step. If not provided, will be auto-computed based on currentStep. */
    status?: 'completed' | 'active' | 'upcoming' | 'error';
    /** Optional custom icon to display instead of number. */
    icon?: ReactNode;
    /** If true, the step cannot be clicked. */
    disabled?: boolean;
}

interface StepItemProps {
    step: Step;
    index: number;
    activeIndex: number;
    size: StepperSize;
    isHorizontal: boolean;
    isLast: boolean;
    isClickable: boolean;
    sizeConfig: SizeConfig;
    colors: StepperColors;
    /** Passed straight through; undefined leaves the badge its own default appearance. */
    badgeAppearance?: StepBadgeProps['appearance'];
    nextStepStatus?: Step['status'];
    onStepClick?: (step: Step, index: number) => void;
    getStepAriaLabel?: (step: Step, index: number) => string;
    stepRef: (el: HTMLDivElement | null) => void;
    dataTestId?: string;
}

export const StepItem = ({
    step,
    index,
    activeIndex,
    size,
    isHorizontal,
    isLast,
    isClickable,
    sizeConfig,
    colors,
    badgeAppearance,
    nextStepStatus,
    onStepClick,
    getStepAriaLabel,
    stepRef,
    dataTestId,
}: StepItemProps) => {
    const computedStatus = getComputedStatus({ step, index, activeIndex });
    const isCurrent = index === activeIndex;
    const isUpcoming = computedStatus === 'upcoming';
    // Two questions, one answer each: `onStepClick` decides whether the steps are controls at all,
    // and `step.disabled` decides whether one of them is out of reach. A stepper that is only read
    // has no control to disable, and dimming its badges would hide the step the reader stands on.
    const isControl = Boolean(onStepClick);
    const innerWrapperPadding = isClickable ? 'p-1' : '';
    const innerWrapperHeight = isHorizontal ? 'h-full' : '';

    const handleClick = () => {
        if (isClickable && onStepClick) {
            onStepClick(step, index);
        }
    };

    const handleStopPropagation = (e: React.MouseEvent) => e.stopPropagation();

    return (
        <div
            ref={stepRef}
            data-testid={dataTestId}
            className={`flex ${isHorizontal ? 'min-w-50 flex-1 flex-row items-stretch' : 'flex-col'} ${sizeConfig.gap}`}
        >
            <div className={`${innerWrapperPadding} ${innerWrapperHeight}`}>
                <div
                    className={`flex flex-1 items-center ${sizeConfig.gap} origin-center transform-gpu rounded-lg bg-white px-4 py-3 shadow-sm ${getInteractionClasses({ isClickable, onStepClick })} ${isHorizontal ? 'h-full' : ''}`}
                    style={isCurrent && isClickable ? { boxShadow: cardBoxShadowLight } : undefined}
                    onClick={handleClick}
                >
                    <div onClick={handleStopPropagation}>
                        <StepBadge
                            step={index + 1}
                            variant={getVariant({ computedStatus })}
                            appearance={badgeAppearance}
                            size={size}
                            onClick={isControl ? handleClick : undefined}
                            disabled={Boolean(step.disabled)}
                            ariaLabel={
                                getStepAriaLabel
                                    ? getStepAriaLabel(step, index)
                                    : `Step ${index + 1}: ${step.label}`
                            }
                            ariaCurrent={isCurrent ? 'step' : undefined}
                            tabIndex={isClickable ? 0 : -1}
                        >
                            {step.icon}
                        </StepBadge>
                    </div>

                    <div className={'flex flex-1 flex-col overflow-hidden'}>
                        <Typography
                            variant={sizeConfig.labelText}
                            color={getLabelColor({ computedStatus, colors })}
                            weight={isUpcoming ? 'regular' : 'medium'}
                            className={'!text-base text-wrap lg:!text-sm'}
                        >
                            {step.label}
                        </Typography>

                        {step.description && (
                            <Typography
                                variant={sizeConfig.descriptionText}
                                color={colors.description}
                                weight={'regular'}
                                className={'text-wrap'}
                            >
                                {step.description}
                            </Typography>
                        )}
                    </div>
                </div>
            </div>

            {!isLast && (
                <div className={`flex ${isHorizontal ? 'items-center' : 'justify-center'}`}>
                    <ConnectorLine
                        isHorizontal={isHorizontal}
                        nextStepStatus={nextStepStatus}
                        colors={colors}
                    />
                </div>
            )}
        </div>
    );
};
